import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExerciseSummary = ({ 
  upcomingExercises = [], 
  overdueExercises = [], 
  completionTrend = [],
  user = null,
  isPremium = false,
  className = ""
}) => {
  const totalUpcoming = upcomingExercises?.length;
  const totalOverdue = overdueExercises?.length;
  
  const formatDueDate = (date) => {
    const today = new Date();
    const dueDate = new Date(date);
    const diffInDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Due today";
    if (diffInDays === 1) return "Due tomorrow";
    if (diffInDays > 1) return `Due in ${diffInDays} days`;
    if (diffInDays === -1) return "1 day overdue";
    return `${Math.abs(diffInDays)} days overdue`;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "text-error bg-error/10 border-error/20",
      medium: "text-warning bg-warning/10 border-warning/20",
      low: "text-success bg-success/10 border-success/20"
    };
    return colors?.[priority] || colors?.medium;
  };

  const getCompletionTrendData = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date?.setDate(date?.getDate() - (6 - i));
      return date?.toLocaleDateString('en-US', { weekday: 'short' });
    });

    return last7Days?.map((day, index) => ({
      day,
      completed: completionTrend?.[index] || 0
    }));
  };

  const trendData = getCompletionTrendData();
  const maxCompleted = Math.max(...trendData?.map(d => d?.completed), 1);

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Exercise Summary</h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Calendar"
          iconPosition="left"
          asChild
        >
          <Link to="/exercise-management">Manage All</Link>
        </Button>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-4 bg-primary/5 rounded-lg">
          <div className="text-2xl font-bold text-primary font-mono">{totalUpcoming}</div>
          <div className="text-sm text-muted-foreground">Upcoming</div>
        </div>
        <div className="text-center p-4 bg-error/5 rounded-lg">
          <div className="text-2xl font-bold text-error font-mono">{totalOverdue}</div>
          <div className="text-sm text-muted-foreground">Overdue</div>
        </div>
      </div>
      {/* Completion Trend Chart */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">7-Day Completion Trend</h4>
        <div className="flex items-end justify-between space-x-1 h-20">
          {trendData?.map((data, index) => (
            <div key={index} className="flex flex-col items-center space-y-1 flex-1">
              <div className="w-full bg-muted rounded-t-sm relative">
                <div
                  className="bg-primary rounded-t-sm transition-all duration-300"
                  style={{
                    height: `${(data?.completed / maxCompleted) * 60}px`,
                    minHeight: data?.completed > 0 ? '4px' : '0px'
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{data?.day}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Upcoming Exercises */}
      {totalUpcoming > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Next Up</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {upcomingExercises?.slice(0, 3)?.map((exercise, index) => (
              <div key={exercise?.id || index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center space-x-2">
                    <h5 className="text-sm font-medium text-foreground truncate">
                      {exercise?.title}
                    </h5>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(exercise?.priority)}`}>
                      {exercise?.priority}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{exercise?.estimatedMinutes} min</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDueDate(exercise?.dueDate)}</span>
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Play"
                  asChild
                >
                  <Link to={`/exercise-management?exercise=${exercise?.id}`}>Start</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Overdue Exercises Alert */}
      {totalOverdue > 0 && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-error" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-error">
                {totalOverdue} Overdue Exercise{totalOverdue > 1 ? 's' : ''}
              </h4>
              <p className="text-xs text-muted-foreground">
                Complete overdue exercises to maintain your progress streak
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
              asChild
            >
              <Link to="/exercise-management?filter=overdue">View All</Link>
            </Button>
          </div>
        </div>
      )}
      {/* Empty State */}
      {totalUpcoming === 0 && totalOverdue === 0 && (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">All Caught Up!</h4>
            <p className="text-sm text-muted-foreground">
              No pending exercises. Great job staying on track!
            </p>
          </div>
          <Button
            variant="outline"
            iconName="Plus"
            iconPosition="left"
            asChild
          >
            <Link to="/ai-plan-generation">Generate New Plan</Link>
          </Button>
        </div>
      )}
      {/* Premium Features Teaser */}
      {!isPremium && (
        <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Crown" size={20} className="text-warning" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground">Premium Exercise Features</h4>
              <p className="text-xs text-muted-foreground">
                Unlock advanced analytics, custom exercises, and priority scheduling
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Crown"
              iconPosition="left"
              asChild
            >
              <Link to="/subscription-management">Upgrade</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseSummary;